/**
 * PowerUpManager - Manages power-up spawning, collection, and effects
 */

import Phaser from 'phaser'
import { PowerUpType, ActivePowerUp, PowerUpConfig, PowerUpSprite } from './types'
import { POWERUP_CONFIG, GAME_HEIGHT } from '../config/game.config'
import { audioManager } from '../audio/AudioManager'

export class PowerUpManager {
  private scene: Phaser.Scene
  private powerUps: Phaser.GameObjects.Group
  private activePowerUps: Map<PowerUpType, ActivePowerUp> = new Map()
  private powerUpTimers: Map<PowerUpType, Phaser.Time.TimerEvent> = new Map()

  // Callbacks for power-up events
  private onPowerUpActivated?: (type: PowerUpType, config: PowerUpConfig) => void
  private onPowerUpDeactivated?: (type: PowerUpType) => void
  private onPowerUpCollected?: (type: PowerUpType, config: PowerUpConfig) => void

  // Shield state
  private shieldHits = 0

  constructor(scene: Phaser.Scene) {
    this.scene = scene
    this.powerUps = scene.add.group()
  }

  /**
   * Set callback for when power-up is activated
   */
  setOnActivated(callback: (type: PowerUpType, config: PowerUpConfig) => void): void {
    this.onPowerUpActivated = callback
  }

  /**
   * Set callback for when power-up is deactivated
   */
  setOnDeactivated(callback: (type: PowerUpType) => void): void {
    this.onPowerUpDeactivated = callback
  }

  /**
   * Set callback for when power-up is collected
   */
  setOnCollected(callback: (type: PowerUpType, config: PowerUpConfig) => void): void {
    this.onPowerUpCollected = callback
  }

  /**
   * Get the power-ups group for collision detection
   */
  getGroup(): Phaser.GameObjects.Group {
    return this.powerUps
  }

  /**
   * Try to spawn a power-up at a given position
   * Called when an alien is destroyed
   */
  trySpawn(x: number, y: number): boolean {
    // Check base spawn chance
    if (Math.random() > POWERUP_CONFIG.baseSpawnChance) {
      return false
    }

    // Don't spawn if too many active power-ups
    if (this.activePowerUps.size >= POWERUP_CONFIG.maxActivePowerUps) {
      return false
    }

    // Select a random power-up type based on spawn chances
    const powerUpType = this.selectRandomType()
    if (!powerUpType) return false

    this.spawn(x, y, powerUpType)
    return true
  }

  /**
   * Select a random power-up type based on spawn chances
   */
  private selectRandomType(): PowerUpType | null {
    const types = Object.entries(POWERUP_CONFIG.types)
    const totalChance = types.reduce((sum, [, config]) => sum + config.spawnChance, 0)
    let random = Math.random() * totalChance

    for (const [type, config] of types) {
      random -= config.spawnChance
      if (random <= 0) {
        return type as PowerUpType
      }
    }

    return PowerUpType.RAPID_FIRE // Fallback
  }

  /**
   * Spawn a specific power-up at position
   */
  spawn(x: number, y: number, type: PowerUpType): void {
    const config = POWERUP_CONFIG.types[type]

    // Create container for power-up visuals
    const container = this.scene.add.container(x, y) as PowerUpSprite
    container.powerUpType = type
    container.config = config as unknown as PowerUpConfig

    // Main body (rounded rectangle shape)
    const size = POWERUP_CONFIG.size
    const body = this.scene.add.graphics()
    body.fillStyle(config.color, 1)
    body.fillRoundedRect(-size / 2, -size / 2, size, size, 6)
    body.lineStyle(2, 0xffffff, 0.8)
    body.strokeRoundedRect(-size / 2, -size / 2, size, size, 6)
    container.add(body)

    // Icon text
    const icon = this.scene.add.text(0, 0, config.icon, {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#ffffff',
    }).setOrigin(0.5)
    container.add(icon)

    // Glow effect
    const glow = this.scene.add.graphics()
    glow.fillStyle(config.color, 0.3)
    glow.fillCircle(0, 0, size * 0.8)
    container.addAt(glow, 0) // Behind main body

    // Pulse animation
    this.scene.tweens.add({
      targets: [glow],
      scaleX: { from: 1, to: 1.3 },
      scaleY: { from: 1, to: 1.3 },
      alpha: { from: 0.3, to: 0.1 },
      duration: 600 / POWERUP_CONFIG.pulseSpeed,
      yoyo: true,
      repeat: -1,
    })

    // Floating animation
    this.scene.tweens.add({
      targets: container,
      y: y + 5,
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    })

    // Add physics
    this.scene.physics.add.existing(container)
    const physicsBody = container.body as Phaser.Physics.Arcade.Body
    physicsBody.setVelocityY(POWERUP_CONFIG.fallSpeed)
    physicsBody.setSize(size, size)

    this.powerUps.add(container)
  }

  /**
   * Handle power-up collection by player
   */
  collect(powerUpSprite: PowerUpSprite): void {
    const type = powerUpSprite.powerUpType
    const config = powerUpSprite.config

    // Play collection sound
    audioManager.playPowerUpPickupSound()

    // Create collection effect
    this.createCollectionEffect(powerUpSprite.x, powerUpSprite.y, config.color)

    // Destroy the sprite
    powerUpSprite.destroy()

    // Callback for collection event
    if (this.onPowerUpCollected) {
      this.onPowerUpCollected(type, config)
    }

    // Activate the power-up
    this.activate(type, config)
  }

  /**
   * Create visual effect when power-up is collected
   */
  private createCollectionEffect(x: number, y: number, color: number): void {
    // Expanding ring
    const ring = this.scene.add.graphics()
    ring.lineStyle(3, color, 1)
    ring.strokeCircle(x, y, 10)

    this.scene.tweens.add({
      targets: ring,
      scaleX: 3,
      scaleY: 3,
      alpha: 0,
      duration: 400,
      ease: 'Power2',
      onComplete: () => ring.destroy(),
    })

    // Sparkle particles
    for (let i = 0; i < 12; i++) {
      const particle = this.scene.add.rectangle(
        x,
        y,
        4,
        4,
        color
      )
      const angle = (i / 12) * Math.PI * 2
      const distance = 40 + Math.random() * 20

      this.scene.tweens.add({
        targets: particle,
        x: x + Math.cos(angle) * distance,
        y: y + Math.sin(angle) * distance,
        alpha: 0,
        scale: 0,
        duration: 400,
        ease: 'Power2',
        onComplete: () => particle.destroy(),
      })
    }
  }

  /**
   * Activate a power-up
   */
  private activate(type: PowerUpType, config: PowerUpConfig): void {
    const now = Date.now()
    const typeConfig = POWERUP_CONFIG.types[type]

    // If already active, refresh duration
    if (this.activePowerUps.has(type)) {
      const existing = this.activePowerUps.get(type)!
      existing.endTime = now + typeConfig.duration

      // Reset timer
      const existingTimer = this.powerUpTimers.get(type)
      if (existingTimer) {
        existingTimer.destroy()
      }
    } else {
      // New activation
      const activePowerUp: ActivePowerUp = {
        type,
        startTime: now,
        endTime: now + typeConfig.duration,
        config,
      }
      this.activePowerUps.set(type, activePowerUp)

      // Play activation sound
      audioManager.playPowerUpActivateSound(type)

      // Callback
      if (this.onPowerUpActivated) {
        this.onPowerUpActivated(type, config)
      }
    }

    // Special handling for shield
    if (type === PowerUpType.SHIELD) {
      this.shieldHits = (typeConfig as { maxHits: number }).maxHits || 1
    }

    // Set deactivation timer
    const timer = this.scene.time.addEvent({
      delay: typeConfig.duration,
      callback: () => this.deactivate(type),
      callbackScope: this,
    })
    this.powerUpTimers.set(type, timer)
  }

  /**
   * Deactivate a power-up
   */
  private deactivate(type: PowerUpType): void {
    this.activePowerUps.delete(type)
    this.powerUpTimers.delete(type)

    // Play deactivation sound
    audioManager.playPowerUpDeactivateSound()

    // Callback
    if (this.onPowerUpDeactivated) {
      this.onPowerUpDeactivated(type)
    }
  }

  /**
   * Check if a power-up is active
   */
  isActive(type: PowerUpType): boolean {
    return this.activePowerUps.has(type)
  }

  /**
   * Get remaining time for an active power-up
   */
  getRemainingTime(type: PowerUpType): number {
    const active = this.activePowerUps.get(type)
    if (!active) return 0
    return Math.max(0, active.endTime - Date.now())
  }

  /**
   * Get all active power-ups
   */
  getActivePowerUps(): ActivePowerUp[] {
    return Array.from(this.activePowerUps.values())
  }

  /**
   * Get the bullet cooldown multiplier
   */
  getBulletCooldownMultiplier(): number {
    if (this.isActive(PowerUpType.RAPID_FIRE)) {
      return (POWERUP_CONFIG.types.rapid_fire as { cooldownMultiplier: number }).cooldownMultiplier
    }
    return 1
  }

  /**
   * Get the number of bullets to fire
   */
  getBulletCount(): number {
    if (this.isActive(PowerUpType.MULTI_SHOT)) {
      return (POWERUP_CONFIG.types.multi_shot as { bulletCount: number }).bulletCount
    }
    return 1
  }

  /**
   * Get bullet spread angle (in radians)
   */
  getBulletSpreadAngle(): number {
    if (this.isActive(PowerUpType.MULTI_SHOT)) {
      const degrees = (POWERUP_CONFIG.types.multi_shot as { spreadAngle: number }).spreadAngle
      return (degrees * Math.PI) / 180
    }
    return 0
  }

  /**
   * Get the alien speed multiplier
   */
  getAlienSpeedMultiplier(): number {
    if (this.isActive(PowerUpType.SLOW_MOTION)) {
      return (POWERUP_CONFIG.types.slow_motion as { speedMultiplier: number }).speedMultiplier
    }
    return 1
  }

  /**
   * Get the score multiplier
   */
  getScoreMultiplier(): number {
    if (this.isActive(PowerUpType.SCORE_MULTIPLIER)) {
      return (POWERUP_CONFIG.types.score_multiplier as { multiplier: number }).multiplier
    }
    return 1
  }

  /**
   * Check if shield is active and absorb hit
   * Returns true if hit was absorbed
   */
  absorbHit(): boolean {
    if (!this.isActive(PowerUpType.SHIELD)) {
      return false
    }

    this.shieldHits--
    audioManager.playShieldHitSound()

    if (this.shieldHits <= 0) {
      // Deactivate shield early
      const timer = this.powerUpTimers.get(PowerUpType.SHIELD)
      if (timer) {
        timer.destroy()
      }
      this.deactivate(PowerUpType.SHIELD)
    }

    return true
  }

  /**
   * Update power-ups (call in game loop)
   */
  update(): void {
    // Remove power-ups that fell off screen
    const powerUpList = this.powerUps.getChildren() as PowerUpSprite[]
    for (const powerUp of powerUpList) {
      if (powerUp.y > GAME_HEIGHT + 30) {
        powerUp.destroy()
      }
    }
  }

  /**
   * Clean up all power-ups (on game restart)
   */
  destroy(): void {
    // Destroy all sprites
    this.powerUps.clear(true, true)

    // Clear all timers
    for (const timer of this.powerUpTimers.values()) {
      timer.destroy()
    }
    this.powerUpTimers.clear()

    // Clear active power-ups
    this.activePowerUps.clear()

    // Reset shield
    this.shieldHits = 0
  }
}
